import { useState } from 'react';
import gettext from '@sdk/lang/gettext';
import { SubscriptionPlanNames } from '@sdk/account/types';
import { AuthType } from '@sdk/authentication/useAuthBuilder';
import { SUBSCRIPTION_NAMES } from '@sdk/common/constants';
import { Feature } from '@sdk/common/features';

import type { OpenPremiumGuardModal, PremiumGuardPassedConfig } from './PremiumGuard';
import getPremiumTooltipId from './getPremiumTooltipId';

type TooltipRefs = {
  // @TODO: type ref to html elements
  [key in string]: any;
};

export type PremiumGuardType = {
  tooltipRefs: TooltipRefs;
  // @TODO: type ref
  addTooltipRef: (tooltipId: string, ref: any) => void;
  openPremiumGuardModal: OpenPremiumGuardModal;
  openPricingModal: () => void;
  openLaborCostVideoModal: () => void;
  getPremiumGuardTooltipContent(premiumFeature?: Feature, hasModal?: boolean): string;
  getRequiredPlan: (premiumFeature?: Feature) => SubscriptionPlanNames;
  getPremiumGuardConfig: (premiumFeature: Feature) => PremiumGuardPassedConfig;
};

export default function usePremiumGuardBuilder(
  auth: AuthType,
  openPremiumGuardModal: OpenPremiumGuardModal,
  openPricingModal: () => void,
  openLaborCostVideoModal: () => void
): PremiumGuardType {
  const { orgSettings, currentUser } = auth;
  const [tooltipRefs, setTooltipRefs] = useState({});

  // TODO there's also _getRequiredPlan implemented in angular realm AND getRequiredSubsctiptionNameForFeature
  const getRequiredPlan = (premiumFeature?: Feature): SubscriptionPlanNames => {
    let planName: SubscriptionPlanNames;
    if (currentUser.org.isToastBilling) {
      return SubscriptionPlanNames.toastSlingPro;
    }
    if (orgSettings.isOldPremium) {
      return SubscriptionPlanNames.premium;
    }
    if (premiumFeature && SUBSCRIPTION_NAMES[orgSettings.premiumFeatures[premiumFeature]]) {
      return SUBSCRIPTION_NAMES[orgSettings.premiumFeatures[premiumFeature]];
    }
    return SubscriptionPlanNames.premium;
  };

  const getPremiumGuardTooltipContent = (premiumFeature?: Feature, hasModal = true): string => {
    const tooltip = gettext('This feature is available in %(requiredPlan)s', {
      requiredPlan: getRequiredPlan(premiumFeature),
    });
    return hasModal ? `${tooltip}. ${gettext('Click to learn more.')}` : tooltip;
  };

  /**
   * Premium modal config used by components to decide what information to display about the feature.
   * @param premiumFeature pass in premium feature that premium guard is guarding
   * @param id allows you to diffrentiate tooltips if there are more to show for the same feature
   */
  const getPremiumGuardConfig = (premiumFeature: Feature, id = ''): PremiumGuardPassedConfig => ({
    hasAccess: auth.isOrgFeatureEnabled(premiumFeature),
    requiredPlan: getRequiredPlan(premiumFeature),
    tooltipContent: getPremiumGuardTooltipContent(premiumFeature),
    tooltipId: getPremiumTooltipId(premiumFeature, id),
  });

  const addTooltipRef = (tooltipId: string, ref: any): void => {
    setTooltipRefs((state) => ({
      ...state,
      [tooltipId]: ref,
    }));
  };

  return {
    openPremiumGuardModal,
    openPricingModal,
    openLaborCostVideoModal,
    getPremiumGuardTooltipContent,
    getRequiredPlan,
    getPremiumGuardConfig,
    tooltipRefs,
    addTooltipRef,
  };
}
